import React, { useCallback, useDeferredValue, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ArrowPathIcon, XMarkIcon } from '@heroicons/react/24/outline';
import * as api from '../components/CaseReview/caseReviewApi';
import {
    DISPOSITION_OPTIONS,
    STATUS_PRIORITY,
    withParsedData
} from '../components/CaseReview/constants';
import CaseStatsBar from '../components/CaseReview/CaseStatsBar';
import CaseFilters from '../components/CaseReview/CaseFilters';
import CaseListItem from '../components/CaseReview/CaseListItem';
import CaseDetailPanel from '../components/CaseReview/CaseDetailPanel';
import PhotoUploadModal from '../components/CaseReview/PhotoUploadModal';

function sortCases(list) {
    return [...list].sort((a, b) => {
        const pa = STATUS_PRIORITY[a.status] || 999;
        const pb = STATUS_PRIORITY[b.status] || 999;
        if (pa !== pb) return pa - pb;
        return new Date(b.created_at) - new Date(a.created_at);
    });
}

export default function CaseReview() {
    const location = useLocation();

    const [cases, setCases] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [lastUpdated, setLastUpdated] = useState(null);
    const [emailTemplates, setEmailTemplates] = useState([]);

    const [selectedCaseId, setSelectedCaseId] = useState(null);
    const [selectedCase, setSelectedCase] = useState(null);
    const [loadingDetails, setLoadingDetails] = useState(false);
    const [showUploadModal, setShowUploadModal] = useState(false);

    // Filters
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [dispositionFilter, setDispositionFilter] = useState('all');
    const [dateFilter, setDateFilter] = useState('all');
    // Defer the search term so typing doesn't re-run the filter on every keystroke.
    const deferredQuery = useDeferredValue(searchQuery);

    const loadCases = useCallback(async () => {
        try {
            const data = await api.fetchCases();
            setCases(sortCases(data.map(withParsedData)));
            setLastUpdated(new Date());
            setError(null);
        } catch (err) {
            setError('Failed to load cases. Please try again.');
        } finally {
            setLoading(false);
        }
    }, []);

    const loadCaseDetails = useCallback(async (caseId) => {
        setLoadingDetails(true);
        try {
            const data = await api.fetchCaseDetails(caseId);
            const parsed = withParsedData(data);
            setSelectedCase(parsed);
            // Keep the matching list row in sync without re-fetching the whole list.
            setCases((prev) =>
                prev.map((c) =>
                    c.qr_case_id === caseId
                        ? { ...c, status: parsed.status, qr_case_data: parsed.qr_case_data, parsedData: parsed.parsedData, photo_count: parsed.photos?.length ?? c.photo_count }
                        : c
                )
            );
        } catch (err) {
            toast.error('Failed to load case details');
        } finally {
            setLoadingDetails(false);
        }
    }, []);

    useEffect(() => {
        loadCases();
        api.fetchEmailTemplates().then(setEmailTemplates).catch(() => {});
    }, [loadCases]);

    // Deep link from notifications / QR (?case= or ?caseId=).
    useEffect(() => {
        if (loading) return;
        const params = new URLSearchParams(location.search);
        const caseId = params.get('case') || params.get('caseId');
        if (caseId && caseId !== selectedCaseId) {
            setSelectedCaseId(caseId);
            loadCaseDetails(caseId);
        }
    }, [location.search, loading, loadCaseDetails, selectedCaseId]);

    const handleSelect = useCallback((caseId) => {
        setSelectedCaseId((prev) => {
            if (prev === caseId) {
                setSelectedCase(null);
                return null;
            }
            loadCaseDetails(caseId);
            return caseId;
        });
    }, [loadCaseDetails]);

    const handleStatusChange = useCallback(async (caseId, status) => {
        // Optimistic local update — no full list refetch.
        setCases((prev) => prev.map((c) => (c.qr_case_id === caseId ? { ...c, status } : c)));
        setSelectedCase((prev) => (prev && prev.qr_case_id === caseId ? { ...prev, status } : prev));
        try {
            await api.updateCaseStatus(caseId, status);
            toast.success('Status updated');
        } catch (err) {
            toast.error('Failed to update status — reverting');
            loadCases();
        }
    }, [loadCases]);

    const refreshSelected = useCallback(async () => {
        if (selectedCaseId) await loadCaseDetails(selectedCaseId);
    }, [selectedCaseId, loadCaseDetails]);

    // Stats computed from already-parsed data.
    const stats = useMemo(() => {
        const statusCounts = {};
        DISPOSITION_OPTIONS.forEach((s) => { statusCounts[s] = 0; });
        let totalReimbursement = 0;
        // Product cost of the units we were actually reimbursed for. unit_cost is
        // joined server-side from the vendor price list; cases whose ASIN has no
        // price-list match contribute nothing and are counted separately so the
        // card can say how complete the figure is.
        let reimbursedUnitCost = 0;
        let reimbursedCases = 0;
        let reimbursedCasesMissingCost = 0;
        cases.forEach((c) => {
            if (c.status in statusCounts) statusCounts[c.status] += 1;
            if (c.status === 'Resolved-Inventory or Refund Processed') {
                const raw = c.parsedData?.amazon_reimbursement_amount;
                if (raw) {
                    const amount = parseFloat(raw.toString().replace(/[$,]/g, ''));
                    if (!isNaN(amount)) totalReimbursement += amount;
                    if (!isNaN(amount) && amount > 0) {
                        reimbursedCases += 1;
                        // The quantity column is 0 on some older cases even though the
                        // case covers a unit — fall back to the JSON qty, then to 1.
                        const units = Number(c.quantity) || Number(c.parsedData?.qty) || 1;
                        const cost = parseFloat(c.unit_cost);
                        if (!isNaN(cost)) reimbursedUnitCost += cost * units;
                        else reimbursedCasesMissingCost += 1;
                    }
                }
            }
        });
        return {
            total: cases.length,
            totalReimbursement,
            reimbursedUnitCost,
            reimbursedCases,
            reimbursedCasesMissingCost,
            ...statusCounts
        };
    }, [cases]);

    const uniqueDispositions = useMemo(() => {
        const set = new Set(cases.map((c) => c.disposition).filter(Boolean));
        return Array.from(set).sort();
    }, [cases]);

    const filteredCases = useMemo(() => {
        const query = deferredQuery.trim().toLowerCase();
        return cases.filter((c) => {
            if (query) {
                const d = c.parsedData || {};
                const fields = [c.qr_case_id, c.disposition, c.status, d.item_name, d.asin1, d.fnsku, d.order_id, d.searchquery, d.amazon_case_id, d.lpn];
                if (!fields.some((f) => f && f.toString().toLowerCase().includes(query))) return false;
            }
            if (statusFilter !== 'all' && c.status !== statusFilter) return false;
            if (dispositionFilter !== 'all' && c.disposition !== dispositionFilter) return false;
            if (dateFilter !== 'all') {
                const days = Math.floor((Date.now() - new Date(c.created_at).getTime()) / (1000 * 60 * 60 * 24));
                if (dateFilter === 'today' && days > 0) return false;
                if (dateFilter === 'week' && days > 7) return false;
                if (dateFilter === 'month' && days > 30) return false;
            }
            return true;
        });
    }, [cases, deferredQuery, statusFilter, dispositionFilter, dateFilter]);

    if (loading) {
        return (
            <div className="container mx-auto px-4 py-6">
                <div className="animate-pulse space-y-4">
                    <div className="h-8 w-64 bg-gray-200 rounded" />
                    <div className="grid grid-cols-2 md:grid-cols-7 gap-4">
                        {Array.from({ length: 7 }).map((_, i) => <div key={i} className="h-20 bg-gray-200 rounded-lg" />)}
                    </div>
                    <div className="h-96 bg-gray-200 rounded-lg" />
                </div>
            </div>
        );
    }

    return (
        <div className="container mx-auto px-4 py-6">
            {/* Header */}
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold text-gray-900">Case Review Dashboard</h1>
                <div className="flex items-center gap-4">
                    {lastUpdated && (
                        <span className="text-sm text-gray-500">Last updated: {lastUpdated.toLocaleTimeString()}</span>
                    )}
                    <button
                        type="button"
                        onClick={loadCases}
                        className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 text-sm font-medium"
                    >
                        <ArrowPathIcon className="h-4 w-4" />
                        Refresh
                    </button>
                </div>
            </div>

            {error && (
                <div className="mb-6 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded flex items-center justify-between">
                    <span>{error}</span>
                    <button type="button" onClick={() => setError(null)} className="text-red-700 hover:text-red-900">
                        <XMarkIcon className="h-5 w-5" />
                    </button>
                </div>
            )}

            <CaseStatsBar stats={stats} statusFilter={statusFilter} onToggleStatus={setStatusFilter} />

            <CaseFilters
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                statusFilter={statusFilter}
                setStatusFilter={setStatusFilter}
                dispositionFilter={dispositionFilter}
                setDispositionFilter={setDispositionFilter}
                dateFilter={dateFilter}
                setDateFilter={setDateFilter}
                uniqueDispositions={uniqueDispositions}
                filteredCount={filteredCases.length}
                totalCount={cases.length}
            />

            {/* Master-detail */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                {/* List */}
                <div className="lg:col-span-5 bg-white rounded-lg shadow overflow-hidden self-start">
                    <div className="p-4 border-b border-gray-200">
                        <h2 className="text-lg font-semibold text-gray-900">Cases</h2>
                    </div>
                    <div className="divide-y divide-gray-100 max-h-[calc(100vh-13rem)] overflow-y-auto">
                        {filteredCases.length === 0 ? (
                            <div className="p-8 text-center text-gray-500">
                                <p className="text-lg font-medium">No cases found</p>
                                <p className="text-sm">Try adjusting your search or filter criteria</p>
                            </div>
                        ) : (
                            filteredCases.map((caseItem) => (
                                <CaseListItem
                                    key={caseItem.qr_case_id}
                                    caseItem={caseItem}
                                    selected={selectedCaseId === caseItem.qr_case_id}
                                    onSelect={handleSelect}
                                />
                            ))
                        )}
                    </div>
                </div>

                {/* Detail */}
                <div className="lg:col-span-7">
                    <div className="bg-white rounded-lg shadow p-6 lg:sticky lg:top-6">
                        <CaseDetailPanel
                            selectedCase={selectedCaseId ? selectedCase : null}
                            loadingDetails={loadingDetails}
                            emailTemplates={emailTemplates}
                            onStatusChange={handleStatusChange}
                            onRefresh={refreshSelected}
                            onOpenUpload={() => setShowUploadModal(true)}
                        />
                    </div>
                </div>
            </div>

            {showUploadModal && selectedCase && (
                <PhotoUploadModal
                    selectedCase={selectedCase}
                    onClose={() => setShowUploadModal(false)}
                    onUploaded={refreshSelected}
                />
            )}
        </div>
    );
}
